import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { colors, Button, Checkbox, Typography } from '@leapeasy/ui-kit';
import ClaimDatePickerInput from 'components/ClaimForm/ClaimDatePickerInput';
import { setTenants, getTenantsAction } from 'store/actions/claimFormActions';
import { Box, CircularProgress } from '@mui/material';
import { Fragment } from 'react';
import {
  formatDateForPicker,
  getTenantDobValue,
  getMaxDateForDob,
  validateTenantDob,
  areAllTenantDobsValid,
  getExpectedResidentNamesFromPrefill,
} from 'components/ClaimForm/helpers';
import { selectClaimFormState } from 'utils/claimFormState';

const FormConfirmTenant = ({ handleBack, handleNext }) => {
  const [tenants, setTenantsState] = useState([]);
  const dispatch = useDispatch();
  const [disableNext, setDisableNext] = useState(true);
  const [dobErrors, setDobErrors] = useState({});
  const formData = useSelector(selectClaimFormState);
  const fetchedPolicyRef = useRef(null);

  useEffect(() => {
    const policyId = formData?.data?.policy;
    if (!policyId) {
      return;
    }

    const tenantList = Array.isArray(formData?.tenants) ? formData.tenants : [];
    if (tenantList.length > 0) {
      fetchedPolicyRef.current = policyId;
      return;
    }

    if (fetchedPolicyRef.current === policyId) {
      return;
    }

    fetchedPolicyRef.current = policyId;
    dispatch(getTenantsAction(policyId));
  }, [dispatch, formData?.data?.policy, formData?.tenants]);

  useEffect(() => {
    const tenantList = Array.isArray(formData?.tenants) ? formData.tenants : [];
    setTenantsState(tenantList);
  }, [formData?.tenants]);

  useEffect(() => {
    if (!tenants.length) {
      setDobErrors({});
      return;
    }

    const maxBirthDate = getMaxDateForDob();
    const nextErrors = {};
    tenants.forEach((tenant, index) => {
      const error = validateTenantDob(getTenantDobValue(tenant), maxBirthDate);
      if (error) {
        nextErrors[index] = error;
      }
    });
    setDobErrors(nextErrors);
  }, [tenants]);

  const handleDobChange = (value, index) => {
    const tenantList = Array.isArray(formData?.tenants) ? [...formData.tenants] : [];
    tenantList.splice(index, 1, {
      ...tenantList[index],
      Date_of_Birth__c_changed: value,
    });
    dispatch(setTenants(tenantList));
    setDobErrors((prev) => ({
      ...prev,
      [index]: validateTenantDob(value, getMaxDateForDob()),
    }));
  };

  const checkDob = () => {
    const tenantList = Array.isArray(formData?.tenants) ? formData.tenants : [];
    if (!tenantList.length) {
      return;
    }

    const maxBirthDate = getMaxDateForDob();
    const nextErrors = {};
    let allValid = true;

    tenantList.forEach((tenant, index) => {
      const value = getTenantDobValue(tenant);
      const error = validateTenantDob(value, maxBirthDate);
      if (error) {
        nextErrors[index] = error;
        allValid = false;
      }
    });

    setDobErrors(nextErrors);

    if (allValid) {
      const normalizedTenants = tenantList.map((tenant) => {
        const value = getTenantDobValue(tenant);
        return {
          ...tenant,
          Date_of_Birth__c_changed: tenant.Date_of_Birth__c_changed || value,
        };
      });
      dispatch(setTenants(normalizedTenants));
      handleNext();
    }
  };

  const maxBirthDateLabel = formatDateForPicker(getMaxDateForDob());
  const tenantsMissingLastName = tenants.some((tenant) => !tenant?.LastName?.trim());
  const allDobsValid = areAllTenantDobsValid(tenants);
  const tenantsLoading = Boolean(formData?.tenantsLoading);
  const expectedResidentNames = getExpectedResidentNamesFromPrefill(formData?.data);
  const hasExpectedResidents = expectedResidentNames.length > 0;

  const renderEmptyResidentsState = () => {
    if (tenantsLoading) {
      return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} mt={3}>
          <CircularProgress size={32} sx={{ color: colors.purple[900] }} />
          <Typography variant="body1" style={{ color: colors.purple[900] }}>
            Loading resident information…
          </Typography>
        </Box>
      );
    }

    if (hasExpectedResidents) {
      return (
        <Box display="flex" flexDirection="column" gap={2} mt={3}>
          <Typography variant="body1" style={{ color: colors.purple[900] }}>
            We could not load resident details for this policy
            {expectedResidentNames.length === 1 ? ` (${expectedResidentNames[0]})` : ''}. Please
            contact <a href="mailto:claims@leapeasy.com">claims@leapeasy.com</a> for assistance.
          </Typography>
          <Box display="flex" justifyContent="center">
            <Button size="medium" variant="secondary" onClick={handleBack}>
              Back
            </Button>
          </Box>
        </Box>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap={2} mt={3}>
        <Typography variant="body1" style={{ color: colors.purple[900] }}>
          There is no resident in this policy
        </Typography>
        <Box display="flex" justifyContent="center">
          <Button size="medium" variant="secondary" onClick={handleBack}>
            Back
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <Fragment>
      <Box display="flex" flexDirection="column" gap="4px">
        {!tenants?.length ? (
          renderEmptyResidentsState()
        ) : (
          <Fragment>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
                mt: 4,
                gap: '16px',
              }}
            >
              <Typography
                variant="body1"
                style={{ fontSize: '16px', color: colors.purple[900], marginBottom: '10px' }}
              >
                Please ensure you confirm or provide the accurate date of birth for the Resident
                listed below. If there are more than one Resident on a Leap policy, you will be
                required to confirm and/or provide the date of birth for each resident.
              </Typography>
              {tenantsMissingLastName && (
                <Typography variant="small" style={{ color: colors.error?.[500] || '#d32f2f' }}>
                  A tenant on this policy is missing a last name in our system. Please contact{' '}
                  <a href="mailto:claims@leapeasy.com">claims@leapeasy.com</a> before continuing.
                </Typography>
              )}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  flexGrow: 1,
                }}
              >
                <label style={{ width: '150px', margin: '10px 0', fontSize: '15px' }}>
                  Residents
                </label>
                <p
                  style={{
                    width: '256px',
                    margin: '10px 0',
                    fontSize: '15px',
                    fontWeight: 'normal',
                  }}
                >
                  Birthday
                </p>
              </Box>
              {tenants.map((tenant, index) => (
                <Box
                  key={tenant.Id || index}
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'start',
                    flexGrow: 1,
                    gap: '5px',
                  }}
                >
                  <label style={{ margin: '10px 0', width: '150px', fontSize: '13px' }}>
                    {`${tenant.FirstName} ${tenant.LastName}`}
                  </label>
                  <Box sx={{ flex: 1, minWidth: 0, width: '100%', maxWidth: 360 }}>
                    <ClaimDatePickerInput
                      label="Date of Birth"
                      value={getTenantDobValue(tenant)}
                      minWidth="100%"
                      maxWidth="100%"
                      minDate="01/01/1900"
                      maxDate={maxBirthDateLabel}
                      size="medium"
                      showActionButtons={false}
                      onChange={(e) => handleDobChange(e.target.value, index)}
                      placeholder="MM/DD/YYYY"
                      fullWidth
                    />
                    {dobErrors[index] && (
                      <Typography
                        variant="small"
                        style={{ color: colors.error?.[500] || '#d32f2f', marginTop: '4px' }}
                      >
                        {dobErrors[index]}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
              <Checkbox
                size="medium"
                value={!disableNext}
                onChange={(confirmed) => setDisableNext(!confirmed)}
                indeterminate={false}
                label=" "
                description="The birthday of the resident is correct?"
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'row',
                mt: 4,
                gap: '16px',
              }}
            >
              <Button
                size="medium"
                variant="secondary"
                onClick={handleBack}
                style={{ flexShrink: 2 }}
              >
                Back
              </Button>

              <Button
                size="medium"
                variant="primary"
                onClick={checkDob}
                style={{ flexShrink: 1 }}
                disabled={disableNext || !tenants.length || !allDobsValid || tenantsMissingLastName}
              >
                Next
              </Button>
            </Box>
          </Fragment>
        )}
      </Box>
    </Fragment>
  );
};

export default FormConfirmTenant;
